import { Document, Page, View, Text, Image, Link, StyleSheet } from "@react-pdf/renderer";
import { formatDateRange } from "../utils/dateRange";
import { experienceBullets } from "../utils/bullets";
import { employmentTypeLabel } from "../admin/collectionsConfig";

// Asegura que la URL tenga protocolo (si no, el enlace no apunta afuera).
const withProtocol = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const L = {
  es: {
    contact: "Medios de Contacto",
    skills: "Resumen de Habilidades",
    langs: "Idiomas",
    profile: "Perfil Personal",
    exp: "Experiencia Laboral",
    edu: "Historial Académico",
    certs: "Certificados y Cursos",
  },
  en: {
    contact: "Contact",
    skills: "Skills Summary",
    langs: "Languages",
    profile: "Profile",
    exp: "Work Experience",
    edu: "Education",
    certs: "Certificates & Courses",
  },
};

const s = StyleSheet.create({
  page: { flexDirection: "row", fontSize: 9, color: "#1a1a1a" },
  side: { width: "34%", backgroundColor: "#2c3e6b", color: "white", padding: 16 },
  main: { width: "66%", padding: 20 },
  name: { fontSize: 22, color: "#2c3e6b", fontWeight: "bold" },
  role: { fontSize: 11, letterSpacing: 2, color: "#2c3e6b", marginBottom: 8 },
  h: { fontSize: 11, color: "#2c3e6b", marginTop: 10, marginBottom: 4, fontWeight: "bold" },
  hSide: { fontSize: 10, marginTop: 14, marginBottom: 6, letterSpacing: 1 },
  photo: { width: 90, height: 90, borderRadius: 45, alignSelf: "center", marginBottom: 12 },
  item: { marginBottom: 6 },
  bold: { fontWeight: "bold" },
  small: { fontSize: 8, color: "#444" },
  companyLink: { color: "#2c3e6b", textDecoration: "none" },
});

export default function CvDocument({ data, language = "es" }) {
  const t = L[language] || L.es;
  const p = data.profile || {};
  const f = (obj, field) => obj?.[`${field}_${language}`] ?? "";
  // "Otros" (categoría other) siempre de última en habilidades.
  const isOther = (c) => String(c || "").toLowerCase() === "other";
  const rawCats = [...new Set((data.skills || []).map((k) => k.category))];
  const cats = [...rawCats.filter((c) => !isOther(c)), ...rawCats.filter(isOther)];

  // Experiencia y estudios se muestran según el campo "order" (lo ordena la
  // consulta), igual que en el sitio.
  const experienceList = data.experience || [];
  const studiesList = data.studies || [];

  // Localize a skill-category slug using the editable categories (else the slug).
  const skillCats = (data.categories || []).filter((c) => c.scope === "skills");
  const catLabel = (slug) => {
    const c = skillCats.find((x) => x.value === slug);
    if (!c) return slug;
    return (language === "es" ? c.label_es : c.label_en) || slug;
  };

  return (
    <Document>
      <Page size="A4" style={s.page}>
        <View style={s.side}>
          {p.photo_url ? <Image src={p.photo_url} style={s.photo} /> : null}
          <Text style={s.hSide}>{t.contact.toUpperCase()}</Text>
          {p.email ? <Text style={s.item}>{p.email}</Text> : null}
          {p.website ? <Text style={s.item}>{p.website}</Text> : null}
          {p.github ? <Text style={s.item}>{p.github}</Text> : null}
          {p.phone ? <Text style={s.item}>{p.phone}</Text> : null}
          {p.linkedin ? <Text style={s.item}>{p.linkedin}</Text> : null}

          <Text style={s.hSide}>{t.skills.toUpperCase()}</Text>
          {cats.map((c) => (
            <Text key={c} style={s.item}>
              <Text style={s.bold}>{catLabel(c)}: </Text>
              {(data.skills || [])
                .filter((k) => k.category === c)
                .map((k) => k.name)
                .join(", ")}
            </Text>
          ))}

          <Text style={s.hSide}>{t.langs.toUpperCase()}</Text>
          {(data.languages || []).map((l, i) => (
            <Text key={i} style={s.item}>
              {f(l, "name")} — {f(l, "level")}
            </Text>
          ))}
        </View>

        <View style={s.main}>
          <Text style={s.name}>{(p.name || "").toUpperCase()}</Text>
          <Text style={s.role}>{f(p, "role").toUpperCase()}</Text>

          <Text style={s.h}>{t.profile.toUpperCase()}</Text>
          <Text>{f(p, "about")}</Text>

          <Text style={s.h}>{t.exp.toUpperCase()}</Text>
          {experienceList.map((e, i) => (
            <View key={i} style={s.item}>
              <Text style={s.bold}>
                {e.url ? (
                  <Link src={withProtocol(e.url)} style={s.companyLink}>
                    {e.company}
                  </Link>
                ) : (
                  e.company
                )}
                {" — "}
                {f(e, "role")}
                {e.employment_type ? ` · ${employmentTypeLabel(e.employment_type, language)}` : ""}
              </Text>
              <Text style={s.small}>
                {formatDateRange(e.date_start, e.date_end, e.current, language)}
                {e.location ? ` · ${e.location}` : ""}
              </Text>
              {experienceBullets(e, language).map((b, bi) => (
                <Text key={bi}>• {b}</Text>
              ))}
            </View>
          ))}

          <Text style={s.h}>{t.edu.toUpperCase()}</Text>
          {studiesList.map((st, i) => (
            <View key={i} style={s.item}>
              <Text style={s.bold}>{st.institution}</Text>
              <Text>
                {f(st, "title")} | {formatDateRange(st.date_start, st.date_end, st.current, language)}
              </Text>
              {f(st, "description") ? (
                <Text style={s.small}>{f(st, "description")}</Text>
              ) : null}
            </View>
          ))}

          <Text style={s.h}>{t.certs.toUpperCase()}</Text>
          {(data.certifications || []).map((c, i) => (
            <Text key={i} style={s.item}>
              • {f(c, "name")} — {c.issuer}
            </Text>
          ))}
        </View>
      </Page>
    </Document>
  );
}
